import React, { useState, useEffect } from 'react';
import ServiceCard from '../../components/ServiceCard/ServiceCard';
import SEO from '../../components/SEO/SEO';
import productAPI from '../../services/productApi';
import './Services.css';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const Services = ({ setCurrentPage, setPageData }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPageNum, setCurrentPageNum] = useState(1);

  // Contact information
  const EMAIL = 'Info@transcontinentalnetwork.com';
  const PHONE_NUMBER = '347.668.2160';
  const ADDRESS = '160-04 Cross Bay Blvd. Howard Beach, NY 11414';

  // Navigation handler for service details
  const handleServiceClick = (serviceId) => {
    setPageData({
      itemId: serviceId,
      itemType: 'service'
    });
    setCurrentPage('details');
  };

  // Email contact handler
  const handleEmailContact = () => {
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=${encodeURIComponent(EMAIL)}`;
    window.open(gmailUrl, '_blank', 'noopener,noreferrer');
  };

  // Phone call handler
  const handlePhoneCall = () => {
    window.location.href = `tel:+13476682160`;
  };

  // Load services from API
  const loadServices = async (categoryFilter = null, page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        per_page: 12,
        page: page
      };

      // Add category filter if selected
      if (categoryFilter && categoryFilter !== 'all') {
        params.category_id = categoryFilter;
      }

      console.log('Loading services with params:', params);

      const response = await productAPI.getServices(params);
      
      if (response && response.success && response.data) {
        console.log('Services loaded successfully:', response.data);
        setServices(response.data.services || []);
        setPagination(response.data.pagination || null);
      } else {
        throw new Error(response?.message || 'Failed to load services');
      }
    } catch (error) {
      console.error('Error loading services:', error);
      const errorInfo = productAPI.handleAPIError(error);
      setError(errorInfo.message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Load categories from API
  const loadCategories = async () => {
    try {
      const response = await productAPI.getCategories({
        active_only: true,
        include_counts: true
      });
      
      if (response && response.success && response.data && response.data.categories) {
        const apiCategories = response.data.categories;
        
        // Enhanced category mapping with comprehensive icons
        const categoryMap = {
          // Security related
          'security cameras': '📹',
          'cameras': '📹',
          'surveillance': '📹',
          'cctv': '📹',
          'video': '📹',
          'ip cameras': '📹',
          
          // NVR/Storage related
          'nvr systems': '💾',
          'nvr': '💾',
          'recording': '💾',
          'storage': '💾',
          'dvr': '💾',
          'network video recorder': '💾',
          
          // Phone systems
          'phone systems': '📞',
          'phones': '📞',
          'voip': '📞',
          'communication': '📞',
          'telephony': '📞',
          'pbx': '📞',
          'voip phones': '📞',
          
          // Access control
          'access control': '🔒',
          'access': '🔒',
          'security': '🔒',
          'door control': '🔒',
          'keypad': '🔒',
          'card reader': '🔒',
          
          // Installation services
          'installation': '🔧',
          'setup': '🔧',
          'mounting': '🔧',
          'wiring': '🔧',
          
          // Monitoring services
          'monitoring': '👁️',
          'surveillance': '👁️',
          'watching': '👁️',
          'guard': '👁️',
          
          // Maintenance
          'maintenance': '⚙️',
          'repair': '⚙️',
          'service': '⚙️',
          'support': '⚙️',
          
          // Networking
          'networking': '🌐',
          'network': '🌐',
          'wifi': '📶',
          'wireless': '📶',
          
          // Accessories
          'accessories': '🔌',
          'equipment': '🔌',
          'hardware': '🔌',
          
          // Cloud services
          'cloud': '☁️',
          'remote': '☁️',
          'online': '☁️'
        };

        // Function to get icon for category
        const getCategoryIcon = (categoryName) => {
          const name = categoryName.toLowerCase();
          
          // Direct match
          if (categoryMap[name]) {
            return categoryMap[name];
          }
          
          // Partial match
          for (const [key, icon] of Object.entries(categoryMap)) {
            if (name.includes(key) || key.includes(name)) {
              return icon;
            }
          }
          
          // Default icon
          return '📋';
        };

        const formattedCategories = [
          { 
            value: 'all', 
            label: 'All Services', 
            icon: '🌐',
            count: apiCategories.reduce((sum, cat) => sum + (cat.service_count || 0), 0)
          },
          ...apiCategories
            .filter(cat => (cat.service_count || 0) > 0)
            .map(cat => ({
              value: cat.id.toString(),
              label: cat.name,
              icon: getCategoryIcon(cat.name),
              count: cat.service_count || 0,
              description: cat.description || null
            }))
        ];

        setCategories(formattedCategories);
      } else {
        throw new Error(response?.message || 'Failed to load categories');
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      const errorInfo = productAPI.handleAPIError(error);
      
      // Set fallback categories if API fails
      setCategories([
        { 
          value: 'all', 
          label: 'All Services', 
          icon: '🌐',
          count: services.length 
        }
      ]);
      
      console.warn('Categories loaded from fallback due to API error:', errorInfo.message);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadCategories();
    loadServices('all', 1);
  }, []);

  // Handle category change
  const handleCategoryChange = (categoryValue) => {
    setSelectedCategory(categoryValue);
    setCurrentPageNum(1);
    const categoryId = categoryValue === 'all' ? null : categoryValue;
    loadServices(categoryId, 1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPageNum(page);
    const categoryId = selectedCategory === 'all' ? null : selectedCategory;
    loadServices(categoryId, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Transform service data for ServiceCard
  const transformService = (service) => {
    console.log('Transforming service:', service);
    
    let imageUrl = null;
    
    if (service.images && Array.isArray(service.images) && service.images.length > 0) {
      imageUrl = service.images[0];
    } else if (service.primary_image) {
      imageUrl = service.primary_image;
    } else if (service.image) {
      imageUrl = service.image;
    } else if (service.image_url) {
      imageUrl = service.image_url;
    }

    let categoryName = 'Service';
    if (service.category && typeof service.category === 'object') {
      categoryName = service.category.name;
    } else if (service.category_name) {
      categoryName = service.category_name;
    }

    const isOnSale = service.is_on_sale && service.sale_price && service.sale_price < service.price;
    const currentPrice = isOnSale ? service.sale_price : service.price;
    const discount = isOnSale ? productAPI.calculateDiscount(service.price, service.sale_price) : 0;

    let priceDisplay;
    if (isOnSale) {
      priceDisplay = `Sale: ${productAPI.formatPrice ? productAPI.formatPrice(currentPrice) : `$${currentPrice}`}`;
    } else {
      priceDisplay = `Starting at ${productAPI.formatPrice ? productAPI.formatPrice(currentPrice) : `$${currentPrice}`}`;
    }

    return {
      id: service.id,
      title: service.name || service.title,
      category: categoryName,
      description: service.description || service.short_description || '',
      image: imageUrl,
      price: priceDisplay,
      features: Array.isArray(service.features) ? service.features : [],
      rating: service.rating || 4.5,
      isOnSale: isOnSale,
      originalPrice: isOnSale ? (productAPI.formatPrice ? productAPI.formatPrice(service.price) : `$${service.price}`) : null,
      discount: discount,
      is_available: service.is_available !== false,
      pricing_model: service.pricing_model || 'fixed',
      duration: service.duration || null
    };
  };

  // Using shared LoadingSpinner component

  // Error component
  const ErrorMessage = ({ message, onRetry }) => (
    <div className="error-message">
      <div className="error-content">
        <h3>⚠️ Error Loading Services</h3>
        <p>{message}</p>
        <button className="btn btn-primary" onClick={onRetry}>
          Try Again
        </button>
      </div>
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="empty-state">
      <div className="empty-content">
        <h3>No Services Found</h3>
        <p>
          {selectedCategory === 'all' 
            ? "No services are currently available." 
            : "No services found in this category."}
        </p>
        {selectedCategory !== 'all' && (
          <button 
            className="btn btn-secondary" 
            onClick={() => handleCategoryChange('all')}
          >
            View All Services
          </button>
        )}
      </div>
    </div>
  );

  // Pagination component
  const PaginationControls = () => {
    if (!pagination || pagination.pages <= 1) return null;

    const pages = [];
    const currentPage = pagination.page;
    const totalPages = pagination.pages;

    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-controls" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        gap: '0.5rem',
        marginTop: '2rem',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="btn btn-secondary"
          style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
        >
          Previous
        </button>

        {pages.map(page => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`btn ${page === currentPage ? 'btn-primary' : 'btn-secondary'}`}
            style={{ minWidth: '40px' }}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="btn btn-secondary"
          style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
        >
          Next
        </button>

        <span style={{ marginLeft: '1rem', fontSize: '0.9rem', color: '#666' }}>
          Page {currentPage} of {totalPages} ({pagination.total || 0} total services)
        </span>
      </div>
    );
  };

  const renderCategoryIcon = (emoji, size = 18) => {
    const strokeWidth = 2.5;
    
    switch (emoji) {
      case '🌐':
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        );
      case '📹':
      case '🎥':
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="16" height="10" rx="2" ry="2" />
            <path d="M18 8l4-3v8l-4-3" />
            <path d="M2 17h16" />
          </svg>
        );
      case '💾':
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
        );
      case '📞':
      case '☎️':
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        );
      case '🔒':
      case '🔐':
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        );
      case '🔧':
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        );
      case '👁️':
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        );
      case '⚙️':
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        );
      case '☁️':
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
          </svg>
        );
      case '🚨':
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 22h20L12 2z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      default:
        return emoji;
    }
  };

  return (
    <div className="services-page">
      <SEO 
        title="Security Camera & CCTV Installation Services | Transcontinental Network"
        description="Comprehensive security camera installation services. 4K IP cameras, NVR setups, night vision surveillance, and commercial IT networking by licensed experts."
        keywords="security camera installation, CCTV installation, IP camera setup, surveillance installation, NVR setup, commercial camera installation, Transcontinental Network"
        canonical="https://transcontinentalnetwork.com/services"
      />
      {/* Page Header */}
      <section className="services-header">
        <div className="container">
          <h1 className="page-title fade-in">Professional Security & Communication Solutions</h1>
          <p className="page-subtitle fade-in">
            Expert video surveillance, NVR systems, and phone installations for your home or business. 
            Licensed, insured, and committed to securing your world.
          </p>
        </div>
      </section>

      {/* Service Categories Filter */}
      {categories.length > 1 && (
        <section className="services-filter">
          <div className="container">
            <div className="filter-tabs">
              {categories.map(category => (
                <button
                  key={category.value}
                  className={`filter-tab ${selectedCategory === category.value ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.value)}
                  disabled={loading}
                >
                  <span className="tab-icon">{renderCategoryIcon(category.icon)}</span>
                  <span className="tab-label">
                    {category.label}
                    {category.count !== undefined && (
                      <span className="tab-count">({category.count})</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PROFESSIONAL WEB DEV & HOSTING PROMO BANNER (White Theme) */}
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="webdev-promo-banner" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f0fdfa 100%)',
          color: '#0f172a',
          borderRadius: '16px',
          padding: '28px 32px',
          marginBottom: '2.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          boxShadow: '0 10px 30px rgba(13, 148, 136, 0.08)',
          border: '1px solid rgba(13, 148, 136, 0.25)',
          position: 'relative',
          overflow: 'hidden',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flex: '1', minWidth: '300px' }}>
            <div style={{
              background: 'rgba(13, 148, 136, 0.1)',
              border: '1px solid rgba(13, 148, 136, 0.25)',
              borderRadius: '12px',
              padding: '14px',
              color: '#0d9488',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 18 22 12 16 6"></polyline>
                <polyline points="8 6 2 12 8 18"></polyline>
              </svg>
            </div>
            <div>
              <span style={{ 
                background: 'rgba(13, 148, 136, 0.08)', 
                color: '#0d9488', 
                padding: '4px 12px', 
                borderRadius: '20px', 
                fontSize: '0.78rem', 
                fontWeight: '700',
                letterSpacing: '1.2px',
                border: '1px solid rgba(13, 148, 136, 0.2)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                </svg>
                NEW SERVICE OFFERING
              </span>
              <h3 style={{ fontSize: '1.45rem', fontWeight: '800', margin: '10px 0 6px', color: '#0f172a', letterSpacing: '-0.3px' }}>
                Custom Web Development &amp; Managed Cloud Hosting
              </h3>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.95rem', lineHeight: '1.5' }}>
                Build high-performance business websites, e-commerce platforms, and zero-downtime cloud hosting with local technical support.
              </p>
            </div>
          </div>
          <button 
            onClick={() => setCurrentPage('web-development')}
            style={{
              background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
              color: '#ffffff',
              padding: '13px 26px',
              borderRadius: '10px',
              fontWeight: '700',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.95rem',
              boxShadow: '0 4px 15px rgba(13, 148, 136, 0.3)',
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>Explore Web &amp; Hosting</span>
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </button>
        </div>
      </div>

      {/* Services Content */}
      <section className="services-grid section">
        <div className="container">
          {loading ? (
            <LoadingSpinner type="page" message="Loading services..." />
          ) : error ? (
            <ErrorMessage 
              message={error} 
              onRetry={() => loadServices(selectedCategory === 'all' ? null : selectedCategory, currentPageNum)} 
            />
          ) : services.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Results count */}
              <div className="results-info" style={{ 
                marginBottom: '1.5rem', 
                textAlign: 'center',
                color: '#666'
              }}>
                <p>
                  Showing {services.length} service{services.length !== 1 ? 's' : ''}
                  {pagination && pagination.total && (
                    <span> of {pagination.total} total</span>
                  )}
                  {selectedCategory !== 'all' && categories.find(c => c.value === selectedCategory) && 
                    ` in ${categories.find(c => c.value === selectedCategory)?.label}`
                  }
                </p>
              </div>

              {/* Services Grid */}
              <div className="grid-2">
                {services.map(service => {
                  const transformedService = transformService(service);
                  
                  return (
                    <ServiceCard 
                      key={service.id} 
                      service={transformedService}
                      onServiceClick={() => handleServiceClick(service.id)}
                    />
                  );
                })}
              </div>

              {/* Pagination */}
              <PaginationControls />
            </>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="services-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Need a Custom Security Solution?</h2>
            <p className="cta-text">
              Don't see exactly what you need? Contact us for custom surveillance and communication solutions 
              tailored to your specific security requirements.
            </p>
            <div className="cta-buttons">
              <button 
                className="btn btn-primary"
                onClick={handleEmailContact}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle', display: 'inline-block' }}>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Email: {EMAIL}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={handlePhoneCall}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle', display: 'inline-block' }}>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Call Now: {PHONE_NUMBER}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Footer */}
      <div className="services-contact-footer">
        <p>
          <strong>Headquarters:</strong> {ADDRESS}
        </p>
        <p>
          <strong>Email:</strong> <a href={`https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=${encodeURIComponent(EMAIL)}`} target="_blank" rel="noopener noreferrer">{EMAIL}</a>
          <span className="divider">|</span>
          <strong>Phone:</strong> <a href={`tel:+13476682160`}>{PHONE_NUMBER}</a>
        </p>
      </div>

      {/* Floating Email Button */}
      <button
        className="email-float"
        onClick={handleEmailContact}
        title="Email us"
      >
        ✉️
      </button>
    </div>
  );
};

export default Services;